import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssetDto, GalleryAccessLevel, GalleryDto, TagDto } from '@picflow/types';
import { Subject, map, merge, mergeMap, of, startWith, switchMap, take, tap } from 'rxjs';
import { AssetTagService } from 'src/app/asset-tag.service';
import { EditTagDialogComponent } from 'src/app/gallery/dialogs/edit-tag-dialog/edit-tag-dialog.component';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionService } from 'src/app/services/permission.service';
import { StateService } from 'src/app/services/state.service';
import { AutofocusDirective } from 'src/app/shared/directives/autofocus.directive';
import { ButtonDirective } from 'src/app/shared/directives/button.directive';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { SortByPipe } from 'src/app/shared/pipes/sort-by.pipe';
import { TagService } from 'src/app/tag.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';

@Component({
  selector: 'app-asset-tags',
  templateUrl: './asset-tags.component.html',
  styleUrls: ['./asset-tags.component.scss'],
  standalone: true,
  imports: [
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    AsyncPipe,
    NgIf,
    SortByPipe,
    SvgComponent,
    ButtonDirective,
    ContextMenuComponent,
    AutofocusDirective,
    TooltipDirective,
  ],
})
export class AssetTagsComponent implements OnChanges {
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  @ViewChild('tagTypeahead') tagTypeahead: ContextMenuComponent;

  addTagForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    category: new FormControl(''),
  });

  public tagService = inject(TagService);
  private assetTagService = inject(AssetTagService);
  private state = inject(StateService);
  private dialog = inject(DialogService);
  private permissionService = inject(PermissionService);

  onTagsChanged$ = new Subject<void>();

  tags$ = merge(this.tagService.tags$, this.onTagsChanged$.pipe(switchMap(() => this.tagService.tags$))).pipe(
    map((tagsFromService: TagDto[]) => {
      const tags: TagDto[] = this.asset['tags'];
      return tags.map(t => tagsFromService.find(tag => tag.id === t.id)).filter(Boolean);
    })
  );

  allAvailableTags$ = this.tagService.tags$.pipe(
    map(tags => {
      return tags.filter(tag => {
        if ((this.asset['tags'] as TagDto[]).find(at => at.id === tag.id)) return false;
        return true;
      });
    })
  );

  filteredTags$ = merge(
    this.addTagForm.valueChanges.pipe(startWith(this.addTagForm.value)),
    this.onTagsChanged$.pipe(switchMap(() => this.addTagForm.valueChanges.pipe(startWith(this.addTagForm.value))))
  ).pipe(
    switchMap(({ name }) => {
      return this.tagService.tags$.pipe(
        map(tags => {
          const input = name.toLowerCase();
          return tags.filter(tag => {
            if ((this.asset['tags'] as TagDto[]).find(at => at.id === tag.id)) return false;
            return tag.name.toLowerCase().includes(input);
          });
        })
      );
    })
  );

  ngOnChanges() {
    this.onTagsChanged$.next();
  }

  async addTag() {
    return this.canCreate$
      .pipe(
        take(1),
        switchMap(canCreate => {
          if (!canCreate) return of(undefined);
          return this.tagService.tags$.pipe(
            take(1),
            tap(tags => {
              const existingTag = tags.find(t => t.name === this.addTagForm.value.name);
              if (existingTag) return this.addTagToAsset(existingTag);
              if (!this.addTagForm.valid) return;
              this.tagService.createTag(this.addTagForm.value.name, this.addTagForm.value.category).subscribe(tag => {
                this.addTagToAsset(tag);
              });
            })
          );
        })
      )
      .subscribe();
  }

  addTagToAsset(tag: TagDto) {
    if (!this.asset['tags'].find(t => t.id === tag.id)) {
      this.assetTagService.add(this.asset.id, tag.id).subscribe(() => {
        if (!this.asset['tags'].find(t => t.id === tag.id)) {
          this.asset['tags'].push(tag);
        }
        this.onTagsChanged$.next();
      });
    }
    this.tagTypeahead?.close();
    this.addTagForm.controls.name.setValue('');
  }

  removeTagFromAsset(tag: TagDto, event: MouseEvent) {
    event.stopPropagation();
    this.assetTagService.remove(this.asset.id, tag.id).subscribe(() => {
      this.asset['tags'] = this.asset['tags'].filter(t => t.id !== tag.id);
      this.onTagsChanged$.next();
    });
    this.tagTypeahead?.close();
    this.addTagForm.controls.name.setValue('');
  }

  get canEditTag() {
    const tenantId = this.gallery['tenant']?.id || this.gallery['tenant'];
    if (!this.state.tenants?.some(t => t.id === tenantId)) return false;
    if (!this.permissionService.hasRolePermission('tags:update')) return false;
    return true;
  }

  editTag(tag: TagDto) {
    if (!this.canEditTag) return;
    this.dialog.open(EditTagDialogComponent, { tag });
  }

  isFocused(el: HTMLElement, event: MouseEvent) {
    event.stopPropagation();
    return el === document.activeElement;
  }

  canCreate$ = this.filteredTags$.pipe(
    mergeMap(() => this.tagService.tags$),
    map(tags => {
      const tenantId = this.gallery['tenant']?.id || this.gallery['tenant'];
      if (!this.state.tenants?.some(t => t.id === tenantId)) return false;
      if (!this.permissionService.hasRolePermission('tags:create')) return false;
      const alreadyExists = tags.some(tag => tag.name.toLowerCase() === this.addTagForm.value.name.toLowerCase());
      if (alreadyExists) return false;
      return true;
    })
  );
}
